/**
 * Safe current-URL extraction for login redirect flows.
 *
 * Mirrors ServerSide/return_url_util.py::extract_safe_return_url — the
 * two utilities apply identical security guards so every call site
 * (main.py, main-debug.py, qlogin.py, QXWSMgr.PoseLoginPanel) produces
 * the same validated return_url shape regardless of entry point.
 *
 * When the app redirects to login mid-session (session expired, SSO
 * failure, auto-reauth), the user's current URL needs to survive the
 * bounce so post-login they land back where they were. PoseLoginPanel
 * preserves the path+query as a return_url param that LoginForm.jsx
 * consumes via sessionStorage.
 *
 * Enterprise SaaS security guards (all must match the Python version):
 *   1. Length cap (2 KB)   — prevents DOS via oversized URL
 *   2. CRLF rejection       — prevents HTTP header injection through
 *                             the eventual Location header
 *   3. Strict "/app/" prefix (trailing slash) — prevents future-route
 *                             prefix collisions like "/approvals"
 *   4. "//" rejection       — blocks protocol-relative and double-slash
 *                             path confusion
 *   5. ".." rejection       — blocks path traversal
 *   6. Path+query only      — caller supplies window.location.pathname
 *                             and window.location.search, never origin
 *                             or href, so the result is always a
 *                             relative URL beginning with "/app/"
 */

const MAX_URL_LEN = 2048;

/**
 * Extract a validated /app/* path+query string from the pieces of the
 * current window.location. Returns the relative URL (e.g.
 * "/app/base/0?tool=filters") or null if any guard fails.
 *
 * @param {string} pathname — typically window.location.pathname
 * @param {string} search   — typically window.location.search (may be empty)
 * @returns {string|null}
 */
export function extractSafeReturnUrl(pathname, search) {
  if (!pathname || typeof pathname !== "string") return null;
  const searchPart = typeof search === "string" ? search : "";
  const full = pathname + searchPart;
  if (full.length > MAX_URL_LEN) return null;
  if (full.indexOf("\r") !== -1 || full.indexOf("\n") !== -1) return null;
  let decodedPath;
  try {
    decodedPath = decodeURIComponent(pathname);
  } catch (e) {
    return null;
  }
  if (decodedPath !== pathname) return null;
  if (!pathname.startsWith("/app/")) return null;
  if (pathname.indexOf("//") !== -1) return null;
  if (pathname.indexOf("..") !== -1) return null;
  return full;
}
